<template>
  <div class="v-flex groups-list">
    <div
      class="status-filters"
      role="group"
      aria-label="Filter groups by status"
    >
      <button
        v-for="option in statusFilters"
        :key="option.value"
        type="button"
        class="status-pill"
        :class="{ 'status-pill--active': statusFilter === option.value }"
        :aria-pressed="String(statusFilter === option.value)"
        :data-cy="`group-status-filter-${option.value}`"
        @click="setStatusFilter(option.value)"
      >
        {{ option.label }}
        <span class="status-pill__count">{{ statusCounts[option.value] }}</span>
      </button>
    </div>

    <DataTable
      :actions="[]"
      :table-data="tableData"
      :busy="loading"
      :on-row-clicked="onRowClicked"
      :paginated="paginated"
      :current-page="currentPage"
      :page-size="pageSize"
      :total-records="matchingGroups.length"
      :page-size-options="PAGE_SIZES"
      :on-page-change="onPageChange"
      :on-page-size-change="onPageSizeChange"
      :no-local-sorting="true"
      :on-sort-changed="onSortChanged"
      class-name="groups-table"
      data-cy="groups-list"
      :fixed-layout="true"
    >
      <template #cell(filters)="data">
        {{ filterLabels(data.item).join(', ') }}
      </template>

      <template #cell(schedule)="data">
        {{ scheduleLabel(data.item.automation) }}
      </template>

      <template #head(visibleGroup)="data">
        {{ data.label }}
        <span
          v-b-tooltip.hover
          class="header-info"
          title="Show this group in the Customers filter, under “Start from a saved group”."
          data-cy="customer-filter-info"
        >
          <Info :size="14" />
        </span>
      </template>

      <!-- Customer filter saves from the list; Active only shows here and changes in the group, behind Save Group. -->
      <template #cell(visibleGroup)="data">
        <input
          class="visibility-checkbox"
          type="checkbox"
          :checked="data.item.visible_group"
          :disabled="data.item.saving"
          :aria-label="`Show ${data.item.name} in the Customers filter`"
          :data-cy="`group-visible-${data.item.pkid}`"
          @change="toggleVisibility(data.item, $event)"
        />
      </template>

      <template #cell(active)="data">
        <span
          class="status-badge"
          :class="`status-badge--${statusTone(data.item)}`"
          :title="statusTitle(data.item)"
          :data-cy="`group-active-${data.item.id}`"
          >{{ activeLabel(data.item) }}</span
        >
      </template>

      <template #cell(actions)="data">
        <button
          type="button"
          class="row-delete-button"
          title="Delete group"
          aria-label="Delete group"
          :data-cy="`group-delete-${data.item.id}`"
          @click="confirmDelete(data.item)"
        >
          <Trash2 :size="16" />
        </button>
      </template>
    </DataTable>
  </div>
</template>

<script lang="ts">
import DataTable from '@/components/DataTable/DataTable.vue'
import endpoints from '@/utils/endpoints.js'
import { cfAxios } from '@/utils/init-axios.js'
import { Condition, parseFilter } from '@/views/MessageCenter/condition.js'
import { DAY_SHORT, Schedule, hasEnded, repeatText } from './schedule'
import { Info, Trash2 } from 'lucide-vue'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

/** The wire shape of core-service's group automation (GET/PUT …/automation). */
interface GroupAutomation {
  template_id: number | null
  is_active: boolean
  days_of_week: number[]
  send_time: string
  schedule?: Schedule | null
  sends_count?: number
}

interface GroupRow {
  id: string
  pkid: number
  name: string
  filter: Record<string, unknown>
  /** Listed under "Start from a saved group" in the Customers filter. */
  visible_group: boolean
  automation: GroupAutomation | null
  /** The automation request failed: the row shows "—" and its switch stays locked. */
  automationFailed: boolean
  saving: boolean
}

// Human label per saved-filter key, for the Filters column. Keys are Condition.Type values.
const FILTER_LABELS: Record<string, string> = {
  [Condition.Type.Overdue]: 'Days past due',
  [Condition.Type.DueDate]: 'Days until due date',
  [Condition.Type.Location]: 'Location',
  [Condition.Type.AccountManager]: 'Account manager',
  [Condition.Type.AccountStatus]: 'Account status',
  [Condition.Type.LastName]: 'Last name',
  [Condition.Type.AccountTags]: 'Tags',
  [Condition.Type.UnConfirmedPromises]: 'Unconfirmed promise',
  [Condition.Type.ConfirmedPromises]: 'Confirmed promise',
  [Condition.Type.Autopay]: 'Autopay',
  [Condition.Type.ScheduledPayment]: 'Scheduled payment',
  [Condition.Type.RegisteredCustomer]: 'Registered',
  [Condition.Type.UnregisteredCustomer]: 'Unregistered',
  [Condition.Type.CollectorAction]: 'Collector action',
  [Condition.Type.ActionDateRange]: 'Action date',
  [Condition.Type.TextEnabled]: 'Text enabled',
  [Condition.Type.EmailEnabled]: 'Email enabled',
  [Condition.Type.AiCallsEnabled]: 'AI calls enabled',
  [Condition.Type.PromiseDateRange]: 'Promise to pay',
  [Condition.Type.ScheduledPaymentDateRange]: 'Scheduled payment date',
  [Condition.Type.Birthday]: 'Birthday',
  [Condition.Type.BirthdayMonth]: 'Birthday',
  [Condition.Type.CreatedDateRange]: 'Created',
  [Condition.Type.DeclinedPayments]: 'Declined payments'
}

type StatusTone = 'on' | 'off' | 'ended' | 'none'
type StatusFilter = 'all' | StatusTone

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'on', label: 'Active' },
  { value: 'off', label: 'Paused' },
  { value: 'ended', label: 'Ended' },
  { value: 'none', label: 'No automation' }
]

// The Active column sorts in this order, the pills' order.
const STATUS_ORDER: StatusTone[] = ['on', 'off', 'ended', 'none']

// The footer shows only once the groups outgrow the smallest page.
const PAGE_SIZES = [25, 50, 100]

const byName = (a: GroupRow, b: GroupRow) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

@Component({
  components: { DataTable, Info, Trash2 },
  computed: { ...mapGetters(['currentMerchant']) }
})
export default class GroupsList extends Vue {
  currentMerchant!: { id: string; pkid: number }

  @Prop({ type: String, default: '' }) search!: string
  @Prop({ type: Number, default: null }) openGroupPkid!: number | null

  loading = false
  groups: GroupRow[] = []
  statusFilter: StatusFilter = 'all'
  statusFilters = STATUS_FILTERS
  sortBy = ''
  sortDesc = false
  currentPage = 1
  pageSize = PAGE_SIZES[0]
  PAGE_SIZES = PAGE_SIZES

  headers = [
    { key: 'name', label: 'Name', sortable: true, thStyle: { width: '25%' } },
    { key: 'filters', label: 'Filters', thStyle: { width: '27%' } },
    {
      key: 'schedule',
      label: 'Schedule',
      sortable: true,
      thStyle: { width: '15%' }
    },
    {
      key: 'visibleGroup',
      label: 'Customer filter',
      sortable: true,
      thStyle: { width: '15%' }
    },
    {
      key: 'active',
      label: 'Active',
      sortable: true,
      thStyle: { width: '12%' }
    },
    { key: 'actions', label: '', thStyle: { width: '6%' } }
  ]

  mounted() {
    this.load()
  }

  // The whole row opens the editor, as on Templates; b-table skips clicks on the checkbox and delete button.
  onRowClicked(group: GroupRow) {
    // Without its automation the editor would save a blank one over the real one.
    if (group.automationFailed) {
      this.$bvToast.toast(
        "This group's automation could not be loaded. Reload the page to edit it.",
        { title: 'Groups', variant: 'warning' }
      )

      return
    }
    this.$emit('edit', group)
  }

  /** Groups, then every group's automation in one batch, from core-service. */
  async load() {
    this.loading = true
    try {
      const merchantPkid = this.currentMerchant.pkid
      const response = await cfAxios.get(endpoints.customerGroups(merchantPkid))
      const groups: GroupRow[] = (response.data?.groups ?? []).map(
        (
          group: Omit<GroupRow, 'automation' | 'automationFailed' | 'saving'>
        ) => ({
          ...group,
          pkid: group.pkid,
          automation: null,
          automationFailed: false,
          saving: false
        })
      )
      if (
        !groups.every(
          group => Number.isSafeInteger(group.pkid) && group.pkid > 0
        )
      ) {
        throw new Error('A customer group has no pkid')
      }
      const batch = await this.batchAutomations(merchantPkid)
      if (batch) {
        for (const group of groups)
          group.automation = batch.get(group.pkid) ?? null
      } else {
        await this.loadEachAutomation(groups)
      }
      if (groups.some(group => group.automationFailed)) {
        this.$bvToast.toast(
          "Some groups' automations could not be loaded. Reload to try again.",
          { title: 'Groups', variant: 'warning' }
        )
      }
      this.groups = groups
      this.openLinkedGroup()
    } catch {
      this.$bvToast.toast('Failed to load groups', {
        title: 'Groups',
        variant: 'danger'
      })
    } finally {
      this.loading = false
    }
  }

  /**
   * Every group's automation by group pkid, in one request; null when it fails or the core-service answering
   * predates the `automation` field, so the list falls back to reading each group's own.
   */
  async batchAutomations(
    merchantPkid: number
  ): Promise<Map<number, GroupAutomation> | null> {
    try {
      const response = await cfAxios.get(
        endpoints.groupAutomations(merchantPkid)
      )
      const rows: Array<{ group_id: number; automation?: GroupAutomation }> =
        response.data?.automations
      if (!Array.isArray(rows) || !rows.every(row => row.automation)) {
        return null
      }

      return new Map(
        rows.map(row => [row.group_id, row.automation as GroupAutomation])
      )
    } catch {
      return null
    }
  }

  // One group's automation failing must not hide every group, so each loads on its own.
  async loadEachAutomation(groups: GroupRow[]) {
    const results = await Promise.allSettled(
      groups.map(group =>
        cfAxios.get(
          endpoints.groupAutomation(this.currentMerchant.pkid, group.pkid)
        )
      )
    )
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        groups[index].automation = result.value.data?.automation ?? null
      } else {
        groups[index].automationFailed = true
      }
    })
  }

  /** The status pill a group falls under; a group whose automation failed is under All only. */
  statusOf(group: GroupRow): StatusTone | null {
    return group.automationFailed ? null : this.statusTone(group)
  }

  /** Pill counts over every group, so they hold still while the search changes. */
  get statusCounts(): Record<StatusFilter, number> {
    const counts = { all: this.groups.length, on: 0, off: 0, ended: 0, none: 0 }
    for (const group of this.groups) {
      const status = this.statusOf(group)
      if (status) counts[status] += 1
    }

    return counts
  }

  // Search runs in the browser over the name and the Filters column: core-service sends every group at once.
  get matchingGroups(): GroupRow[] {
    const needle = this.search.trim().toLowerCase()

    return this.groups.filter(
      group =>
        (this.statusFilter === 'all' ||
          this.statusOf(group) === this.statusFilter) &&
        (!needle ||
          [group.name, ...this.filterLabels(group)].some(text =>
            text.toLowerCase().includes(needle)
          ))
    )
  }

  get sortedGroups(): GroupRow[] {
    const compare = this.sortCompare()
    if (!compare) return this.matchingGroups
    const direction = this.sortDesc ? -1 : 1

    return [...this.matchingGroups].sort(
      (a, b) => direction * compare(a, b) || byName(a, b)
    )
  }

  sortCompare(): ((a: GroupRow, b: GroupRow) => number) | null {
    switch (this.sortBy) {
      case 'name':
        return byName
      case 'schedule':
        return (a, b) =>
          this.scheduleLabel(a.automation).localeCompare(
            this.scheduleLabel(b.automation)
          )
      case 'visibleGroup':
        return (a, b) => Number(b.visible_group) - Number(a.visible_group)
      case 'active': {
        const rank = (group: GroupRow) => {
          const status = this.statusOf(group)

          return status ? STATUS_ORDER.indexOf(status) : STATUS_ORDER.length
        }

        return (a, b) => rank(a) - rank(b)
      }
      default:
        return null
    }
  }

  get paginated(): boolean {
    return this.matchingGroups.length > PAGE_SIZES[0]
  }

  get tableData() {
    const start = (this.currentPage - 1) * this.pageSize

    return {
      headers: this.headers,
      items: this.paginated
        ? this.sortedGroups.slice(start, start + this.pageSize)
        : this.sortedGroups
    }
  }

  setStatusFilter(filter: StatusFilter) {
    this.statusFilter = filter
    this.currentPage = 1
  }

  onSortChanged({ sortBy, sortDesc }: { sortBy?: string; sortDesc?: boolean }) {
    this.sortBy = sortBy || ''
    this.sortDesc = Boolean(sortDesc)
    this.currentPage = 1
  }

  onPageChange(page: number) {
    this.currentPage = page
  }

  onPageSizeChange(size: number) {
    this.pageSize = size
    this.currentPage = 1
  }

  // A `?group=` that arrives after the rows loaded (the browser's Forward) opens the group too.
  @Watch('openGroupPkid')
  openLinkedGroup() {
    const linked = this.groups.find(group => group.pkid === this.openGroupPkid)
    if (linked) this.onRowClicked(linked)
  }

  @Watch('search')
  onSearch() {
    this.currentPage = 1
  }

  filterLabels(group: GroupRow): string[] {
    return parseFilter(group.filter).map((condition: Condition) => {
      const { min, max } = condition as {
        min?: number | null
        max?: number | null
      }
      // Single-day ranges (every converted reminder) read as the day itself.
      if (condition.type === Condition.Type.DueDate) {
        return (condition as { text: string }).text
      }
      if (condition.type === Condition.Type.Overdue && min && min === max) {
        return `Days past due: ${min}`
      }
      const label = FILTER_LABELS[condition.type] ?? condition.type
      const value = (condition as { short?: string | null }).short

      return value ? `${label}: ${value}` : label
    })
  }

  isActive(group: GroupRow): boolean {
    return Boolean(group.automation?.is_active)
  }

  // A finished custom schedule cannot be switched back on: it would send nothing and stop again.
  // Switched off too: on its last send day an ending schedule is still on, and can be paused.
  isEnded(group: GroupRow): boolean {
    return hasEnded(group.automation) && !group.automation?.is_active
  }

  /** The status pill's look: on, off (paused), ended, or none (no automation, or it did not load). */
  statusTone(group: GroupRow): StatusTone {
    if (!group.automation) return 'none'
    if (this.isEnded(group)) return 'ended'

    return this.isActive(group) ? 'on' : 'off'
  }

  statusTitle(group: GroupRow): string {
    if (group.automationFailed) {
      return "This group's automation could not be loaded. Reload to try again."
    }
    if (this.isEnded(group)) {
      return 'This schedule has ended. Edit the schedule to restart it.'
    }

    return 'Open the group to change it.'
  }

  activeLabel(group: GroupRow): string {
    if (group.automationFailed) return '—'
    if (!group.automation) return 'No automation'
    if (this.isEnded(group)) return 'Ended'

    return this.isActive(group) ? 'Active' : 'Paused'
  }

  scheduleLabel(automation: GroupAutomation | null): string {
    if (automation?.schedule) return repeatText(automation.schedule, true)
    if (!automation?.days_of_week?.length) return '—'
    const days = [...automation.days_of_week].sort()
    const is = (expected: number[]) =>
      days.length === expected.length && days.every((d, i) => d === expected[i])
    if (is([0, 1, 2, 3, 4, 5, 6])) return 'Daily'
    if (is([0, 1, 2, 3, 4])) return 'Weekdays'
    if (is([5, 6])) return 'Weekends'

    return days.map(day => DAY_SHORT[day]).join(', ')
  }

  // The Templates page's Quick Send checkbox: the box flips at once and flips back if the save fails.
  async toggleVisibility(group: GroupRow, event?: Event) {
    const checkbox = event?.target as HTMLInputElement | undefined
    group.saving = true
    const visible = !group.visible_group
    try {
      const result = await cfAxios.put(
        endpoints.customerGroupVisibility(
          this.currentMerchant.pkid,
          group.pkid
        ),
        { visible_group: visible }
      )
      group.visible_group = result.data?.visible_group ?? visible
    } catch {
      if (checkbox) checkbox.checked = group.visible_group
      this.$bvToast.toast('Failed to update the group', {
        title: 'Groups',
        variant: 'danger'
      })
    } finally {
      group.saving = false
    }
  }

  async confirmDelete(group: GroupRow) {
    const ok = await this.$bvModal.msgBoxConfirm(
      `Delete "${group.name}"? Its automation stops with it.`,
      { title: 'Delete group', okVariant: 'danger', okTitle: 'Delete' }
    )
    if (!ok) return
    try {
      await cfAxios.delete(
        endpoints.customerGroup(this.currentMerchant.pkid, group.pkid)
      )
      this.groups = this.groups.filter(row => row.pkid !== group.pkid)
      const pages = Math.ceil(this.matchingGroups.length / this.pageSize)
      this.currentPage = Math.max(1, Math.min(this.currentPage, pages))
    } catch {
      this.$bvToast.toast('Failed to delete the group', {
        title: 'Groups',
        variant: 'danger'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// ::v-deep leads: the table sits inside DataTable, so it never carries this component's scope id.
::v-deep .groups-table {
  tbody tr {
    cursor: pointer;
  }

  // The switch and the delete button are a hair taller than a line of text; centre every cell on them.
  td {
    vertical-align: middle;
  }
}

// A copy of the Templates page's .automation-pill strip (MessageTemplateListView.vue): keep the two in step.
.status-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  // The toolbar's 25px plus #search-group's 1em, so the first pill lines up under the search box.
  margin: 0 25px 0 calc(25px + 1em);
  padding-bottom: 12px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 14px;
  color: $darker-gray;

  font-size: 14px;
  font-weight: 600;
  line-height: 1;

  background: transparent;
  border: 1px solid transparentize($darker-gray, 0.55);
  border-radius: 999px;
  cursor: pointer;
  transition:
    color 0.15s,
    background-color 0.15s,
    border-color 0.15s;

  &:hover:not(.status-pill--active) {
    color: $blue;

    border-color: $button-blue;
  }

  &:focus-visible {
    outline: 2px solid $bright-blue;
    outline-offset: 2px;
  }

  &--active {
    color: #fff;

    background: $button-blue;
    border-color: $button-blue;
  }

  .status-pill__count {
    color: $darker-gray;

    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }

  &--active .status-pill__count {
    color: #fff;
  }
}

.header-info {
  margin-left: 2px;

  color: $darker-gray;

  vertical-align: text-top;
  cursor: help;
}

// The Active pill's blue, at the size of the table's text.
.visibility-checkbox {
  width: 16px;
  height: 16px;
  margin-left: 5px;

  vertical-align: middle;
  accent-color: $button-blue;
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;

  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;

  border: 1px solid transparent;
  border-radius: 999px;

  &--on {
    color: #fff;

    background: $button-blue;
  }

  &--off {
    color: $darker-gray;

    background: $lighter-gray;
  }

  // Ended is finished, not paused: an outline. No automation has nothing set up: a dashed one.
  &--ended {
    color: $darker-gray;

    border: 1px solid $darker-gray;
  }

  &--none {
    color: $darker-gray;

    border: 1px dashed $darker-gray;
  }
}

.row-delete-button {
  padding: 4px;
  color: $darker-gray;

  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    color: $danger;
  }
}
</style>
